import getConfig from './settings';
import pt from 'prop-types';

// Adds CSS class
export function appendHtmlClass(attr, cls) {
  if (!cls) return attr;
  if (!attr) attr = '';
  attr += (attr.length > 0 ? ' ' : '') + cls;
  return attr;
}

// Extends props from source component with overwrites in types and defaults objects
export function extendProps(source, props, types, defaults) {
  if (props === void 0) {
    props = [];
  }
  if (types === void 0) {
    types = null;
  }
  if (defaults === void 0) {
    defaults = null;
  }
  var result = {
    propTypes: {},
    defaultProps: {}
  };
  if (typeof source == 'object') {
    var _copy = function _copy(source, keys, overwrites) {
      var r = {};
      keys.forEach(function (i) {
        if (source.hasOwnProperty(i)) r[i] = source[i];
      });
      return r;
    }; // eslint-disable-next-line
    if (source.hasOwnProperty('propTypes') && typeof source.propTypes == 'object') result.propTypes = _copy(source.propTypes, props);
    if (source.hasOwnProperty('defaultProps') && typeof source.defaultProps == 'object') result.defaultProps = _copy(source.defaultProps, props);
  }

  // eslint-disable-next-line
  if (typeof types == 'object') Object.assign(result.propTypes, types);
  if (typeof defaults == 'object') Object.assign(result.defaultProps, defaults);
  return result;
}

// Validates color prop against defined palette
export function colorPropType(isRequired, props, propName, componentName) {
  var PALETTE = getConfig('PALETTE');
  var LIGHTNESS_SCALE = getConfig('PALETTE_LIGHTNESS_SCALE');
  var OPACITY_SCALE = getConfig('PALETTE_OPACITY_SCALE');
  var prop = props[propName];
  var isArray = Array.isArray(prop);
  var isString = typeof prop == 'string';
  if (isRequired && !isArray && !isString || !isRequired && prop && !isArray && !isString || isString && Object.keys(PALETTE).indexOf(prop) < 0 || isArray && (prop.length < 1 || prop.length > 3 || Object.keys(PALETTE).indexOf(prop[0]) < 0 || prop.length > 1 && LIGHTNESS_SCALE.filter(function (i) {
    return i.key === prop[1];
  }).length <= 0 || prop.length > 2 && OPACITY_SCALE.filter(function (i) {
    return i.key === prop[2];
  }).length <= 0)) {
    return new Error("Invalid prop " + propName + " supplied to " + componentName + ". Validation failed.");
  }
}

// Validates any type of prop, that can have value dependent on breakpoint agains provided validator function
export function responsivePropType(validator, props, propName, componentName) {
  var prop = props[propName];
  var responsive = false;
  if (typeof prop == 'object') {
    var BREAKPOINT = getConfig('BREAKPOINT');
    var allowed = [];
    responsive = true;
    for (var i in BREAKPOINT) {
      if (BREAKPOINT.hasOwnProperty(i)) allowed = allowed.concat([i, i + "Only", i + "Down"]);
    }
    for (var _i in prop) {
      if (prop.hasOwnProperty(_i) && allowed.indexOf(_i) < 0) {
        responsive = false;
        break;
      }
    }
    if (responsive) {
      for (var _i2 in prop) {
        var _pt$checkPropTypes;
        var result = pt.checkPropTypes((_pt$checkPropTypes = {}, _pt$checkPropTypes[_i2] = validator, _pt$checkPropTypes), prop, _i2, componentName);
        if (typeof result == 'object' && result instanceof 'Error') return result;
        return;
      }
    }
  }
  if (!responsive) {
    var _pt$checkPropTypes2;
    return pt.checkPropTypes((_pt$checkPropTypes2 = {}, _pt$checkPropTypes2[propName] = validator, _pt$checkPropTypes2), props, propName, componentName);
  }
}

// Validates prop against eligible breakpoint values
export function breakpointPropType(props, propName, componentName) {
  var prop = props[propName];
  var BREAKPOINT = getConfig('BREAKPOINT');
  var allowed = [];
  for (var i in BREAKPOINT) {
    if (BREAKPOINT.hasOwnProperty(i)) allowed = allowed.concat([i, i + "Only", i + "Down"]);
  }
  if (allowed.indexOf(prop) < 0) {
    return new Error("Invalid prop " + propName + " supplied to " + componentName + ". No such breakpoint. Validation failed.");
  }
}

// Adds CSS classes for prop, that may depend on breakpoint e.g. resulting in "text-left" or "small-text-left medium-text-right" string
export function appendResponsiveHtmlClass(cls, prop, clsPrefix) {
  if (clsPrefix === void 0) {
    clsPrefix = '';
  }
  if (typeof prop === 'string') {
    cls = appendHtmlClass(cls, "" + clsPrefix + prop);
  } else {
    for (var i in prop) {
      if (prop.hasOwnProperty(i)) cls = appendHtmlClass(cls, i + "-" + clsPrefix + prop[i]);
    }
  }
  return cls;
}

/**
 * Returns multiplied base grid unit as CSS value containing CSS variable
 * @param {*} multiplier 
 * @param {string} unit - (bgu|px)
 */
export function getSpaceValue(multiplier, unit) {
  if (unit === void 0) {
    unit = 'bgu';
  }
  var BASE_GRID_UNIT = getConfig('BASE_GRID_UNIT');
  switch (unit) {
    case 'px':
      var matches = BASE_GRID_UNIT.value.match(/^(\d*(\.\d+)?)px$/);
      return matches.length > 1 ? parseFloat(matches[1]) * multiplier : 0;
    default:
      return "calc(var(" + BASE_GRID_UNIT.variable + ") * " + multiplier + ")";
  }
}

/**
 * Returns true if code is executed on server side
 */
export function isServer() {
  return !(typeof window !== 'undefined' && window.document && window.document.createElement);
}

/**
 * Returns border prop type
 * @param {} props 
 * @param {*} propName 
 * @param {*} componentName 
 */
export function borderPropType(props, propName, componentName) {
  var BORDER_TYPE = getConfig('BORDER_TYPE');
  var prop = props[propName];
  var isArray = Array.isArray(prop);
  if (!prop) return;
  if (prop && !isArray || prop.length < 1 || prop.length > 3 || Object.keys(BORDER_TYPE).indexOf(prop[0]) < 0) {
    return new Error("Invalid prop " + propName + " supplied to " + componentName + ". Validation failed.");
  }
  if (prop.length < 2) return;
  var colorProps = {};
  var colorProp = prop.slice(1);
  colorProps[propName] = colorProp;
  return colorPropType(true, colorProps, propName, componentName);
}
;

/**
 * Returns animation duration based on its name
 * @param {string} name 
 */
export function getAnimationDuration(name) {
  if (name === void 0) {
    name = null;
  }
  var duration = getConfig('ANIMATION_DURATION');
  if (!name || !duration.hasOwnProperty(name)) name = 'fast';
  var value = duration[name];
  var matches = value.match(/(\d+(?:\.\d+)?)(ms|s)/);
  if (matches.length < 3) return 0;
  return parseFloat(matches[1]) / (matches[2] === 'ms' ? 1000 : 1);
}

/**
 * Returns animation easing function name or cubic bezier curve points based on its name
 * See: https://www.framer.com/api/motion/types/#tween.ease
 * @param {string} name 
 */
export function getAnimationEasing(name) {
  var easing = getConfig('ANIMATION_EASING');
  if (!name || !easing.hasOwnProperty(name)) name = 'ease';
  var value = easing[name];
  if (value.indexOf('cubic-bezier(') === 0) {
    var matches = value.match(/\d+(?:\.\d+)?/g);
    if (matches.length >= 4) value = matches.slice(0, 4).map(function (i) {
      return parseFloat(i);
    });
  }
  return value;
}

/**
 * Returns normalized overwrites prop
 * @param {object} overwrites 
 */
export function getOverwrites(overwrites, componentName) {
  if (componentName === void 0) {
    componentName = null;
  }
  if (typeof overwrites !== 'object') return {};
  if (typeof componentName === 'string') return overwrites.hasOwnProperty(componentName) && typeof overwrites[componentName] === 'object' ? overwrites[componentName] : {};
  var result = {};
  for (var i in overwrites) {
    if (!overwrites.hasOwnProperty(i)) continue;
    result[i] = getOverwrites(overwrites, i);
  }
  return result;
}

/**
 * Groups same code execution into single call
 * @param {*} fn 
 * @param {*} ms 
 */
export function debounce(fn, ms) {
  if (ms === void 0) {
    ms = 300;
  }
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function later() {
      timeout = null;
      fn.apply(context, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, ms);
  };
}

/**
 * Limits frequency of executed code
 * @param {*} fn
 * @param {*} ms 
 */
export function throttle(fn, ms) {
  if (ms === void 0) {
    ms = 300;
  }
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function later() {
      timeout = false;
    };
    if (!timeout) {
      fn.apply(context, args);
      timeout = true;
      setTimeout(later, ms);
    }
  };
}

/**
 * Returns element for React portal
 */
export function getPortalTarget() {
  var PORTAL_CONTAINER_ID = getConfig('PORTAL_CONTAINER_ID');
  return document.getElementById(PORTAL_CONTAINER_ID);
}